import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import trackingService, { UntrackedCsxRow } from '../../services/tracking.service';
import { TanTable } from '../../components/TanTable';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';

/**
 * Tracing → Untracked CSX page.
 *
 * CSX is terminal-scoped, so the register sweep skips CSX imports whose
 * pickup-leg origin can't be resolved to a CSX terminal — they never get a
 * tracking link, so they can't appear on the Not Tracking (stale-links)
 * page. This surfaces them, bucketed by cause:
 *   - Needs terminal mapping → map the pickup location (CSX Terminals admin).
 *   - Needs origin location → open the pro and fix the order.
 * TanTable gives search / per-column filter / sort / Excel export; the Pro #
 * opens the order drawer.
 */

const csxLocationLabel = (r: UntrackedCsxRow): string => {
  if (r.location_name) {
    const where = [r.location_city, r.location_state].filter(Boolean).join(', ');
    const base = where ? `${r.location_name} (${where})` : r.location_name;
    return `${base} · recno ${r.pickup_location_recno}`;
  }
  return r.pickup_location_recno != null ? `recno ${r.pickup_location_recno}` : '—';
};

const UntrackedCsx: React.FC = () => {
  const [rows, setRows] = useState<UntrackedCsxRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawerPro, setDrawerPro] = useState<number | null>(null);

  const openDrawer = useCallback((pronumber: number) => setDrawerPro(pronumber), []);
  const closeDrawer = useCallback(() => setDrawerPro(null), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await trackingService.listUntrackedCsx();
      setRows([...data.needs_mapping, ...data.needs_origin_location]);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load untracked CSX orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const proCell = useCallback((v: number | null | undefined) => (
    v != null ? (
      <button
        type="button"
        onClick={() => openDrawer(Number(v))}
        className="text-blue-600 dark:text-blue-400 hover:underline"
        title="Open order"
      >
        {v}
      </button>
    ) : <span className="text-gray-400">—</span>
  ), [openDrawer]);

  const dash = (v: string | null) => v || <span className="text-gray-400">—</span>;

  const columns = useMemo<ColumnDef<UntrackedCsxRow>[]>(() => [
    { accessorKey: 'pronumber', header: 'Pro #', cell: (i) => proCell(i.getValue() as number), size: 100 },
    {
      id: 'reason', header: 'Issue',
      accessorFn: (r) => (r.reason === 'needs_mapping' ? 'Needs terminal mapping' : 'Needs origin location'),
      cell: (i) => {
        const needsMapping = i.row.original.reason === 'needs_mapping';
        return (
          <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${needsMapping
            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
            : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'}`}>
            {needsMapping ? 'Needs terminal mapping' : 'Needs origin location'}
          </span>
        );
      },
      size: 180,
    },
    { accessorKey: 'container', header: 'Container', cell: (i) => dash(i.getValue() as string | null), size: 140 },
    { accessorKey: 'bol', header: 'BOL', cell: (i) => dash(i.getValue() as string | null), size: 170 },
    {
      id: 'pickup', header: 'Pickup location',
      accessorFn: (r) => (r.reason === 'needs_mapping' ? csxLocationLabel(r) : ''),
      cell: (i) => (i.row.original.reason === 'needs_mapping'
        ? <span>{csxLocationLabel(i.row.original)}</span>
        : <span className="text-gray-400">—</span>),
      size: 260,
    },
    {
      id: 'action', header: '', enableSorting: false, enableColumnFilter: false,
      cell: (i) => (i.row.original.reason === 'needs_mapping'
        ? <a href="/tracing/csx-terminals" className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded transition-colors">Map terminal</a>
        : <span className="text-xs text-muted">Fix the order</span>),
      size: 130,
    },
  ], [proCell]);

  return (
    <div className="p-4 pb-24">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-heading">Untracked CSX</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            CSX imports the sweep can't track yet — they never got a tracking link, so they
            don't show on Not Tracking. <strong>Needs terminal mapping</strong> → map the pickup
            location; <strong>Needs origin location</strong> → open the pro and fix the order.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-medium rounded-md transition-colors"
        >
          <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <div className="text-sm text-red-800 dark:text-red-200">{error}</div>
        </div>
      )}

      <TanTable
        data={rows}
        columns={columns}
        loading={loading}
        settingsKey="untracked-csx"
        cardTitle="Untracked CSX orders"
        searchPlaceholder="Search untracked CSX…"
        enableSearch={true}
        enableColumnFilters={true}
        enableShowHideCols={true}
        enableReset={true}
        enableExcelExport={true}
        enableZebraStriping={true}
        showGridLines={true}
        disableVirtualization={true}
        columnFilterTypes={{
          pronumber: 'text', reason: 'dropdown',
          container: 'text', bol: 'text', pickup: 'text',
        }}
        defaultSorting={[{ id: 'reason', desc: false }]}
      />

      <ProDetailSlideOver
        pronumber={drawerPro}
        isOpen={drawerPro !== null}
        onClose={closeDrawer}
      />
    </div>
  );
};

export default UntrackedCsx;
